import React from 'react'
import { useNavigate } from 'react-router-dom'
import { motion } from 'framer-motion'
import { Check, X, ChevronRight, Star, Users, BookOpen, Video, MessageCircle, Zap, Crown } from 'lucide-react'
import useStore from '../store'

const PACKAGES = [
  {
    id: 'basic',
    name: '套餐一',
    price: 299,
    originalPrice: 599,
    tag: '内容交付',
    tagColor: 'bg-blue-100 text-blue-700',
    icon: BookOpen,
    features: [
      { text: '简历优化3份（按目标岗位）', included: true },
      { text: '央企求职情报1年（日更）', included: true },
      { text: '目标岗位面试问题清单', included: true },
      { text: '面试问答参考', included: true },
      { text: '面试经典词汇解读（30个）', included: true },
      { text: '面试问题逻辑解读（视频）', included: true },
      { text: '面试实战陪练', included: false },
      { text: '面试复盘', included: false },
      { text: '试用期陪跑', included: false }
    ],
    deliverables: '在线查看 + 下载',
    cta: '立即购买',
    popular: false
  },
  {
    id: 'pro',
    name: '套餐二',
    price: 1999,
    originalPrice: 3999,
    tag: '全过程实战',
    tagColor: 'bg-purple-100 text-purple-700',
    icon: Users,
    features: [
      { text: '套餐一全部内容', included: true, highlight: true },
      { text: '专人搜集、一对一精准推荐最新招聘信息', included: true },
      { text: '求职通关全过程的学习指导', included: true },
      { text: '3次面试实战陪练（一对一）', included: true },
      { text: '3次面试复盘（一对一）', included: true },
      { text: '顾问月度接单上限保障', included: true },
      { text: '优先预约权', included: true },
      { text: '试用期陪跑', included: false },
      { text: 'AI系统学习', included: false }
    ],
    deliverables: '在线 + 人工服务',
    cta: '选择套餐二',
    popular: true
  },
  {
    id: 'vip',
    name: '套餐三',
    price: 7999,
    originalPrice: 15999,
    tag: '深度陪跑',
    tagColor: 'bg-amber-100 text-amber-700',
    icon: Crown,
    features: [
      { text: '套餐二全部内容', included: true, highlight: true },
      { text: '拿到offer后开启服务', included: true },
      { text: 'AI系统学习培训', included: true },
      { text: '试用期一对一陪跑', included: true },
      { text: '月服务上限10次', included: true },
      { text: '长期跟进至通过试用期', included: true },
      { text: '专属顾问7x24响应', included: true }
    ],
    deliverables: '深度陪跑服务',
    cta: '选择套餐三',
    popular: false
  }
]

const PackageCard = ({ pkg, index }) => {
  const navigate = useNavigate()
  const { addPurchasedPackage } = useStore()
  const Icon = pkg.icon

  const handlePurchase = () => {
    navigate(`/payment?type=package&packageId=${pkg.id}&amount=${pkg.price}`)
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.15 }}
      className={`card overflow-hidden ${pkg.popular ? 'ring-2 ring-primary-500 relative' : ''}`}
    >
      {pkg.popular && (
        <div className="absolute top-0 right-0 bg-primary-500 text-white text-xs font-bold px-3 py-1 rounded-bl-lg">
          最受欢迎
        </div>
      )}

      <div className="p-6">
        {/* 头部 */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-3">
            <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${pkg.popular ? 'bg-primary-100' : 'bg-gray-100'}`}>
              <Icon size={20} className={pkg.popular ? 'text-primary-600' : 'text-gray-600'} />
            </div>
            <div>
              <h3 className="font-bold text-gray-900">{pkg.name}</h3>
              <span className={`text-xs px-2 py-0.5 rounded-full ${pkg.tagColor}`}>{pkg.tag}</span>
            </div>
          </div>
        </div>

        {/* 价格 */}
        <div className="mb-6">
          <div className="flex items-baseline space-x-2">
            <span className="text-3xl font-bold text-gray-900">¥{pkg.price}</span>
            <span className="text-sm text-gray-400 line-through">¥{pkg.originalPrice}</span>
          </div>
          <p className="text-xs text-gray-500 mt-1">交付形式：{pkg.deliverables}</p>
        </div>

        {/* 功能列表 */}
        <div className="space-y-3 mb-6">
          {pkg.features.map((feature, i) => (
            <div key={i} className={`flex items-start space-x-3 ${feature.highlight ? 'bg-primary-50 p-2 rounded-lg -mx-2' : ''}`}>
              {feature.included ? (
                <Check size={18} className="text-green-500 shrink-0 mt-0.5" />
              ) : (
                <X size={18} className="text-gray-300 shrink-0 mt-0.5" />
              )}
              <span className={`text-sm ${feature.included ? 'text-gray-700' : 'text-gray-400'}`}>
                {feature.text}
              </span>
            </div>
          ))}
        </div>

        {/* CTA按钮 */}
        <motion.button
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          onClick={handlePurchase}
          className={`w-full py-3 rounded-lg font-bold transition-colors flex items-center justify-center ${
            pkg.popular
              ? 'bg-primary-600 text-white hover:bg-primary-700'
              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
          }`}
        >
          {pkg.cta}
          <ChevronRight size={18} className="ml-1" />
        </motion.button>
      </div>
    </motion.div>
  )
}

const PackagesPage = () => {
  return (
    <div className="max-w-6xl mx-auto">
      {/* 页面标题 */}
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="text-center mb-12">
        <h1 className="section-title">选择你的求职助力方案</h1>
        <p className="section-subtitle">从内容交付到深度陪跑，找到最适合你的服务</p>
      </motion.div>

      {/* 套餐对比 */}
      <div className="grid md:grid-cols-3 gap-6">
        {PACKAGES.map((pkg, index) => (
          <PackageCard key={pkg.id} pkg={pkg} index={index} />
        ))}
      </div>

      {/* 套餐差异说明 */}
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.5 }}
        className="mt-12 card p-6 md:p-8">
        <h2 className="text-xl font-bold text-gray-900 mb-6">套餐差异说明</h2>

        {/* 方案一 */}
        <div className="mb-8 pb-8 border-b border-gray-100">
          <div className="flex items-center mb-3">
            <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center mr-3">
              <BookOpen size={20} className="text-blue-600" />
            </div>
            <div>
              <h3 className="font-bold text-gray-900">方案一：情报补给包（¥299）</h3>
              <p className="text-sm text-blue-600">适合"自学能力强，只缺内部信息差"的同学</p>
            </div>
          </div>
          <div className="ml-13 pl-13">
            <p className="text-sm text-gray-700 mb-2"><span className="font-medium">你的现状：</span>预算有限，或者本身硬件条件不错，但不懂央企规矩，网上资料鱼龙混杂，不知道哪里是真干货。</p>
            <p className="text-sm text-gray-700 mb-2"><span className="font-medium">你面临的痛点：</span>笔试/面试像开盲盒，不知道考官到底按什么标准打分。</p>
            <p className="text-sm text-gray-700"><span className="font-medium">这个方案能给你什么：</span>撕开信息壁垒。一整年的央企求职情报、避坑清单、体制内高频词汇和面试逻辑拆解。你买的是一套标准化的"备考弹药库"，能不能打胜仗，看你自己的吸收转化能力。</p>
          </div>
        </div>

        {/* 方案二 */}
        <div className="mb-8 pb-8 border-b border-gray-100">
          <div className="flex items-center mb-3">
            <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center mr-3">
              <Users size={20} className="text-purple-600" />
            </div>
            <div>
              <h3 className="font-bold text-gray-900">方案二：全过程实战营（¥1999）</h3>
              <p className="text-sm text-purple-600">适合"从网申到终面，全流程需要有人带路"的同学</p>
            </div>
          </div>
          <div className="ml-13 pl-13">
            <p className="text-sm text-gray-700 mb-2"><span className="font-medium">你的现状：</span>不仅面试容易露怯、有"学生气"，而且在整个求职过程中没人商量——不知道去哪找靠谱岗位，网申怎么填，遇到突发情况不知道问谁。</p>
            <p className="text-sm text-gray-700 mb-2"><span className="font-medium">你面临的痛点：</span>闭门造车，容易在没意识到的地方走弯路、踩暗坑，缺乏"体制内职场思维"。</p>
            <p className="text-sm text-gray-700 mb-3"><span className="font-medium">这个方案能给你什么：</span>阶段性保姆。除了方案一的全部资料，你还将获得：</p>
            <ul className="list-disc list-inside text-sm text-gray-700 mb-3 space-y-1 ml-2">
              <li><span className="font-medium text-purple-700">专人搜集、一对一精准推荐央国企"最新招聘信息"</span>（告别海投瞎搜，帮你省下精力，全力专攻面试实战，并掌握AI应用等高阶求职新技能）；</li>
              <li><span className="font-medium text-purple-700">求职通关全过程的学习指导</span>，导师制定专属练习计划，少走弯路，尽可能一步到位；</li>
              <li><span className="font-medium text-purple-700">3次1对1高压实战陪练 + 3次深度复盘</span>，告别自嗨和自卑，获得真自信。</li>
            </ul>
            <div className="bg-purple-50 rounded-lg p-4">
              <p className="text-sm text-purple-800 leading-relaxed">
                <span className="font-bold">服务承诺：</span>限制顾问每月接单量并赋予你优先预约权，确保老师有精力解答你的个性化问题。你买的是一个"有求必应的求职私人教练"。
              </p>
            </div>
          </div>
        </div>

        {/* 方案三 */}
        <div className="mb-8 pb-8 border-b border-gray-100">
          <div className="flex items-center mb-3">
            <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center mr-3">
              <Crown size={20} className="text-amber-600" />
            </div>
            <div>
              <h3 className="font-bold text-gray-900">方案三：通关护身符（¥7999）</h3>
              <p className="text-sm text-amber-600">适合"自身有硬伤，或极度求稳，不允许失败"的同学</p>
            </div>
          </div>
          <div className="ml-13 pl-13">
            <p className="text-sm text-gray-700 mb-2"><span className="font-medium">你的现状：</span>学历/专业有短板需要曲线救国；或是零经验跨行小白；亦或是家里托了关系，绝对不能在试用期翻车。</p>
            <p className="text-sm text-gray-700 mb-2"><span className="font-medium">你面临的痛点：</span>安全感不足。你怕的不仅是考不上，更怕千辛万苦进去了，因为"不懂人情世故、不擅为人处世"倒在试用期。</p>
            <p className="text-sm text-gray-700 mb-3"><span className="font-medium">这个方案能给你什么：</span>全程兜底。在方案二（拿到Offer前）的全部服务基础上，拿到Offer后才算真正开启核心服务。</p>
            <ul className="list-disc list-inside text-sm text-gray-700 mb-3 space-y-1 ml-2">
              <li><span className="font-medium text-amber-700">专人搜集、一对一精准推荐央国企"最新招聘信息"</span>（优先锁定竞争小、匹配度极高的隐藏优质岗位）；</li>
              <li><span className="font-medium text-amber-700">提供7x24小时专属顾问响应、AI系统培训、试用期1对1贴身陪跑</span>（最多10次/月）。</li>
            </ul>
            <p className="text-sm text-gray-700"><span className="font-medium">你买的不是求职技巧，而是一份"转正险"，确保你端稳铁饭碗。</span></p>
          </div>
        </div>

        {/* 升级提示 */}
        <div className="bg-primary-50 rounded-xl p-5">
          <p className="text-sm text-primary-800 leading-relaxed">
            <span className="font-bold">💡 升级提示：</span>如果你还在犹豫，可以先从 ¥299 的情报包开始，看看我们对央企规则的拆解是否专业。如果在看资料的过程中发现自己搞不定面试，再随时补差价升级到实战或陪跑服务。
          </p>
        </div>
      </motion.div>

      {/* 风险提示 */}
      <div className="mt-8 text-center text-xs text-gray-400">
        <p>所有服务旨在提升求职竞争力，不承诺保offer。服务开通后不支持退款，请谨慎选择。</p>
      </div>
    </div>
  )
}

export default PackagesPage
